import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebase";
import {
  getCareEvidenceReview,
  reviewCareEvidence,
} from "./sourceLifecycleService";
import CareRecordHistoryButton from "./CareRecordHistoryButton";

const ReviewItem = ({ item, canReview, onResolved }) => {
  const [text, setText] = useState(item.summary),
    [editing, setEditing] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const resolve = async (action) => {
    setSaving(true);
    setError("");
    try {
      await reviewCareEvidence({
        collection: item.collection,
        id: item.id,
        token: item.evidenceReview.token,
        action,
        ...(action === "revise" ? { summary: text } : {}),
      });
      onResolved();
    } catch (failure) {
      setError(failure.message || "This review could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const changes = Object.entries(item.evidenceReview.sources || {});
  const histories = [
    ...new Map(
      changes
        .flatMap(([key, value]) => {
          const [collection, id] = key.split(":");
          if (["dailyLogs", "dailyCare", "incidents"].includes(collection))
            return [{ id, collection }];
          return (
            value.entries ||
            (value.entryIds || []).map((id) => ({
              id,
              collection: "dailyLogs",
            }))
          );
        })
        .map((entry) => [`${entry.collection}:${entry.id}`, entry]),
    ).values(),
  ];
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Typography fontWeight={700}>{item.title}</Typography>
        <Typography>{item.summary}</Typography>
        <Alert severity="warning">
          Source edited or deleted — review needed. This fact has not been
          automatically removed or changed.
        </Alert>
        {item.evidenceReview.unaffectedSourceCount > 0 ? (
          <Typography variant="body2">
            {item.evidenceReview.unaffectedSourceCount} other supporting sources
            remain.
          </Typography>
        ) : null}
        {histories.map((entry) => (
          <CareRecordHistoryButton
            key={`${entry.collection}:${entry.id}`}
            entry={entry}
          />
        ))}
        {editing ? (
          <TextField
            label="Revised fact"
            multiline
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {canReview ? (
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
            <Button disabled={saving} onClick={() => resolve("keep")}>
              {item.collection === "medications"
                ? "Confirm plan still correct"
                : "Confirm fact still correct"}
            </Button>
            {item.collection === "knowledgeItems" ? (
              <>
                <Button
                  disabled={saving}
                  onClick={() =>
                    editing ? resolve("revise") : setEditing(true)
                  }
                >
                  {editing ? "Save revised fact" : "Revise fact"}
                </Button>
                <Button disabled={saving} onClick={() => resolve("archive")}>
                  Archive fact
                </Button>
              </>
            ) : null}
            {item.collection !== "knowledgeItems" ? (
              <Typography variant="caption">
                Use the existing medication or goal editor for changes to this
                record.
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2">
            A Care Owner or Partner can complete this review.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
const CareEvidenceReviewPanel = ({ childId }) => {
  const [items, setItems] = useState([]),
    [canReview, setCanReview] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!childId) {
      setItems([]);
      return undefined;
    }
    setItems([]);
    setCanReview(false);
    setError("");
    let active = true;
    const load = () =>
      getCareEvidenceReview({ childId })
        .then((result) => {
          if (active) {
            setItems(result.items || []);
            setCanReview(result.canReview);
            setError("");
          }
        })
        .catch((failure) => {
          if (active)
            setError(failure.message || "Source reviews could not be checked.");
        });
    let unsubscribe = () => {};
    try {
      unsubscribe = onSnapshot(
        doc(db, "children", childId),
        load,
        (failure) => {
          if (active)
            setError(failure.message || "Source reviews could not be checked.");
        },
      );
    } catch (failure) {
      setError(failure.message || "Source reviews could not be checked.");
    }
    return () => {
      active = false;
      unsubscribe();
    };
  }, [childId]);
  const reload = () =>
    getCareEvidenceReview({ childId })
      .then((result) => setItems(result.items || []))
      .catch((failure) => setError(failure.message));
  if (!items.length && !error) return null;
  return (
    <Stack spacing={1.2} sx={{ my: 2 }} aria-label="Changed source reviews">
      <Typography variant="h6">Sources changed — review needed</Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {items.map((item) => (
        <ReviewItem
          key={`${item.collection}:${item.id}:${item.evidenceReview.token}`}
          item={item}
          canReview={canReview}
          onResolved={reload}
        />
      ))}
    </Stack>
  );
};
export default CareEvidenceReviewPanel;
